import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { ReactNode } from 'react';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { BrandS } from '@sorare/core/src/atoms/typography';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText/index.tsx';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { isType } from '@sorare/core/src/lib/gql';
import { getRewardImage } from '@sorare/core/src/lib/rewards';

import { UsdRewardAmount } from '#src/components/missions/DailyTask/DecisivePlayerPickerTaskStatus/ChooseYourRewardStory/UsdRewardAmount/index.tsx';

import type { RewardChip_anyRewardConfigInterface } from './__generated__/index.graphql.ts';

const REWARD_ICON_PX = 24;

const rewardAmount = (
  rewardConfig: RewardChip_anyRewardConfigInterface
): ReactNode => {
  if (isType(rewardConfig, 'MonetaryRewardConfig')) {
    return (
      <UsdRewardAmount
        variant="monetary"
        monetaryAmount={rewardConfig.monetaryAmount}
      />
    );
  }
  if (isType(rewardConfig, 'ConversionCreditRewardConfig')) {
    return (
      <UsdRewardAmount
        variant="conversionCredit"
        percentageDiscount={rewardConfig.percentageDiscount}
        monetaryAmount={rewardConfig.maxDiscount}
      />
    );
  }
  if (isType(rewardConfig, 'CardShardRewardConfig')) {
    return rewardConfig.quantity;
  }
  if (isType(rewardConfig, 'InGameCurrencyRewardConfig')) {
    return rewardConfig.amount;
  }
  return <RewardConfigText anyRewardConfig={rewardConfig} />;
};

type Props = {
  rewardConfig: RewardChip_anyRewardConfigInterface;
};

export const RewardChip = ({ rewardConfig }: Props) => {
  const { sport } = useSportContext();
  const { fiatCurrency } = useCurrentUserContext();

  const icon = getRewardImage(rewardConfig, REWARD_ICON_PX, fiatCurrency);

  const amount = rewardAmount(rewardConfig);

  const flavourIconUrl =
    isType(rewardConfig, 'CardShardRewardConfig') && sport === Sport.FOOTBALL
      ? rewardConfig.flavour?.iconUrl
      : undefined;

  return (
    <Horizontal gap={1} center>
      <img src={icon} alt="" className="size-3 object-contain" />
      <BrandS className="whitespace-nowrap">{amount}</BrandS>
      {!!flavourIconUrl && (
        <img src={flavourIconUrl} alt="" className="size-3 object-contain" />
      )}
    </Horizontal>
  );
};

RewardChip.fragments = {
  anyRewardConfigInterface: gql`
    fragment RewardChip_anyRewardConfigInterface on AnyRewardConfigInterface {
      id
      ...getRewardImage_anyRewardConfigInterface
      ...RewardConfigText_anyRewardConfigInterface
      ... on ConversionCreditRewardConfig {
        id
        percentageDiscount
        maxDiscount {
          ...UsdRewardAmount_monetaryAmount
        }
      }
      ... on InGameCurrencyRewardConfig {
        id
        amount
      }
      ... on MonetaryRewardConfig {
        id
        monetaryAmount: amount {
          ...UsdRewardAmount_monetaryAmount
        }
      }
      ... on CardShardRewardConfig {
        id
        quantity
        flavour {
          slug
          iconUrl
        }
      }
    }
    ${getRewardImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${UsdRewardAmount.fragments.monetaryAmount}
  ` as TypedDocumentNode<RewardChip_anyRewardConfigInterface>,
};
